// 「새로운 레이블리」 환영 여정 상태 — RPC welcome_state() 한 번(본인 JWT). 기획 docs/ops/new-laeebly-welcome-plan.md §6
// useTierRolloutNotice 와 같은 계약: 모듈 캐시 5분 + 진행 중 요청 합치기. 실패하면 null — 페이지로 보내지 않는다(안내가 앱을 막으면 안 된다).
import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { normalizeWelcomeState } from "../lib/welcomeRules";

const TTL_MS = 5 * 60 * 1000;
let cache = null; // { email, at, state }
let inflight = null;

export function _resetWelcomeCache() {
  cache = null;
  inflight = null;
}

export async function fetchWelcomeState(email, { force = false } = {}) {
  if (!force && cache && cache.email === email && Date.now() - cache.at < TTL_MS) return cache.state;
  if (inflight && inflight.email === email) return inflight.promise;
  const promise = (async () => {
    const { data, error } = await supabase.rpc("welcome_state");
    if (error) throw error;
    const state = normalizeWelcomeState(data);
    cache = { email, at: Date.now(), state };
    inflight = null;
    return state;
  })().catch((err) => {
    inflight = null;
    throw err;
  });
  inflight = { email, promise };
  return promise;
}

/** 인트로를 끝까지 봤다 — 실패해도 조용히(다음 방문에 다시 보는 것뿐) */
export async function markWelcomeIntroSeen(version) {
  try {
    const { error } = await supabase.rpc("mark_welcome_intro_seen", { p_version: version || null });
    if (error) throw error;
    if (cache?.state) cache = { ...cache, state: { ...cache.state, introSeen: true } };
  } catch (e) {
    console.warn("인트로 확인 기록 실패:", e?.message || e);
  }
}

/** 「동의하고 새 레이블리 시작」 — 서버 한 트랜잭션. 성공하면 캐시를 acked 로 */
export async function ackWelcome({ version, consents, items, variant = "A" }) {
  const { data, error } = await supabase.rpc("ack_welcome", { p_version: version, p_consents: consents || [], p_items: items || {}, p_variant: variant });
  if (error) throw error;
  if (cache?.state) cache = { ...cache, state: { ...cache.state, acked: true, ackedAt: data?.acked_at || new Date().toISOString() } };
  return data;
}

export default function useWelcomeState(email, { enabled = true } = {}) {
  const [state, setState] = useState(() => (cache && cache.email === email ? cache.state : null));
  const [loading, setLoading] = useState(Boolean(enabled && email));
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!enabled || !email) {
      setState(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchWelcomeState(email, { force: tick > 0 })
      .then((s) => { if (!cancelled) setState(s); })
      .catch((err) => {
        if (!cancelled) {
          console.warn("환영 여정 상태 조회 실패:", err?.message || err);
          setState(null);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [email, enabled, tick]);

  const refresh = useCallback(() => setTick((t) => t + 1), []);
  return { state, loading, refresh };
}
